import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useParams } from "react-router-dom";
import {
  GovReport,
  getReport,
  governanceErrorMessage,
  governanceErrorStatus,
  useGovernanceSettings,
} from "../../../utils/icGovernance";
import Breadcrumb from "./detail/Breadcrumb";
import FieldGrid from "./detail/FieldGrid";
import SideCard from "./detail/SideCard";
import SuccessBanner from "./detail/SuccessBanner";
import { GovSettingsOrNull, SuccessNotice } from "./detail/types";
import { FAINT, TEXT, todayLabel } from "./detail/ui";
import {
  CARD_BORDER,
  OverallStatusChip,
  PRIMARY,
  ReportStatusChip,
  SUPPORT_BASE,
  dateOnly,
  dateTime,
  partyLabel,
} from "./governanceStatus";
import AcknowledgeCard from "./report/AcknowledgeCard";
import { DecisionsTable, ReportLines, RisksTable } from "./report/ReportSections";

const LIST_URL = `${SUPPORT_BASE}/weekly-status`;
const LIST_LABEL = "Weekly Project Status";

const ReportBreadcrumb = ({ last }: { last: string }) => (
  <Breadcrumb to={LIST_URL} label={LIST_LABEL} trail={["Support", LIST_LABEL, last]} />
);

// /dashboard/support/weekly-status/:reportId — one published weekly
// report, read-only, in the workbook's section order, with Acknowledge when
// `permissions.acknowledge`.
const WeeklyReport = () => {
  const { reportId = "" } = useParams();
  const { settings } = useGovernanceSettings();
  const gov: GovSettingsOrNull = settings.enabled ? settings : null;
  const [report, setReport] = useState<GovReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [notice, setNotice] = useState<SuccessNotice | null>(null);
  const requestIdRef = useRef(0);
  const currentIdRef = useRef<string | null>(null);

  const load = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    return getReport(reportId)
      .then((r) => {
        if (requestId !== requestIdRef.current) return;
        currentIdRef.current = r.id;
        setReport(r);
        setError(null);
        setNotFound(false);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to load weekly report:", err);
        if (governanceErrorStatus(err) === 404) {
          currentIdRef.current = null;
          setNotFound(true);
          setReport(null);
        } else {
          setError(governanceErrorMessage(err));
        }
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [reportId]);

  useEffect(() => {
    currentIdRef.current = null;
    setReport(null);
    setNotice(null);
    setNotFound(false);
    setError(null);
    load();
  }, [load]);

  const reload = useCallback(() => {
    load();
  }, [load]);

  const handleAcknowledged = useCallback((r: GovReport) => {
    if (!r || r.id !== currentIdRef.current) return;
    requestIdRef.current++;
    setReport(r);
    setError(null);
    setLoading(false);
    setNotice({ text: `Report acknowledged · ${todayLabel()}` });
  }, []);

  if (loading && !report) {
    return (
      <Box sx={{ padding: "20px", textAlign: "left" }}>
        <ReportBreadcrumb last="Report" />
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress sx={{ color: PRIMARY, mt: 6 }} />
        </Box>
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box sx={{ padding: "20px", textAlign: "left" }}>
        <ReportBreadcrumb last="Report" />
        <Paper elevation={0} sx={{ border: CARD_BORDER, borderRadius: "12px", p: 4, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: TEXT }}>
            Report not found
          </Typography>
          <Typography variant="body2" sx={{ color: FAINT, mt: 1 }}>
            This weekly report does not exist or has not been published.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!report) {
    return (
      <Box sx={{ padding: "20px", textAlign: "left" }}>
        <ReportBreadcrumb last="Report" />
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          {error || "Could not load this report."}
        </Alert>
      </Box>
    );
  }

  const week = dateOnly(report.weekEnding);

  return (
    <Box sx={{ padding: "20px", textAlign: "left", maxWidth: 980 }}>
      <ReportBreadcrumb last={`Week ending ${week}`} />
      <SuccessBanner notice={notice} onClose={() => setNotice(null)} />
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: "wrap", minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: TEXT, mr: 0.5 }}>
            Week ending {week}
          </Typography>
          <OverallStatusChip status={report.overallStatus} />
          <ReportStatusChip status={report.status} />
        </Stack>
        <Tooltip title="Refresh">
          <span>
            <IconButton aria-label="Refresh report" onClick={reload} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Stack spacing={2} sx={{ mt: 2 }}>
        <Paper elevation={0} sx={{ border: CARD_BORDER, borderRadius: "12px", p: 2 }}>
          <FieldGrid
            fields={[
              { label: "Lead Party", value: partyLabel(report.leadParty, gov) },
              {
                label: "Published",
                value: report.publishedAt
                  ? `${report.publishedBy || "—"} · ${dateTime(report.publishedAt)}`
                  : report.publishedBy,
              },
            ]}
          />
        </Paper>

        <SideCard title="Summary">
          <Typography variant="body2" sx={{ whiteSpace: "pre-line", overflowWrap: "anywhere", color: TEXT }}>
            {report.summary || "No summary."}
          </Typography>
        </SideCard>

        <SideCard title="Completed This Week">
          <ReportLines lines={report.completed} />
        </SideCard>

        <SideCard title="Planned Next Week">
          <ReportLines lines={report.planned} />
        </SideCard>

        <SideCard title="Decision Needed">
          <DecisionsTable decisions={report.decisions} />
        </SideCard>

        <SideCard title="Risk / Blocker">
          <RisksTable risks={report.risks} settings={gov} />
        </SideCard>

        <AcknowledgeCard
          key={report.id}
          report={report}
          settings={gov}
          onAcknowledged={handleAcknowledged}
          onReload={reload}
        />
      </Stack>
    </Box>
  );
};

export default WeeklyReport;
